/**
 * One part lifted out as a score of its own.
 *
 * This is what a player is given: the flute line, alone, with everything the piece says
 * about time still around it. So the global timeline is carried over untouched -- the
 * metre, the key, the tempo, the repeats, voltas and jumps, the rehearsal marks, the
 * pickup -- because none of that belongs to a part and all of it has to be read from the
 * stand. Only the other parts go.
 *
 * One thing is printed differently, and it is the thing that makes a part a part: runs of
 * silent bars are gathered into a single bar with a count over it. See `multirest.ts`.
 *
 * It is a plain function rather than a command because it does not change the score it is
 * given: it answers a second document. The two are separate from that moment on, and this
 * package has no link between them, so an edit made to a part is not an edit to the score
 * it came from.
 */

import { createId } from "./ids";
import type { PartId } from "./ids";
import type { Score } from "./types";

/** The part's own title: the piece, then whose copy this is. */
function titleFor(title: string, name: string): string {
  const piece = title.trim();
  return piece === "" ? name : `${piece} — ${name}`;
}

/**
 * The score holding only the named part, or the reason there is not one.
 *
 * The ids of the music are kept as they are. A part is the same music as the passage it
 * was taken from, and two documents are free to name it the same way; nothing here reads
 * an id from another document, and the clipboard mints fresh ones when a passage is
 * pasted, so the two can be open at once without colliding.
 *
 * The page comes over as it is, except that the part gathers its rests. A player reading
 * one line is told to count thirty bars; a conductor reading twenty needs them to line up,
 * so the score it came from is left as it was.
 *
 * Groups do not come with it. A bracket or a brace says that several parts belong
 * together, and with one part left there is nothing for it to say. A piano keeps its brace
 * regardless, because that follows from a part having two staves rather than from a group.
 */
export function extractPart(score: Score, id: PartId): Score | string {
  const part = score.parts.find((candidate) => candidate.id === id);
  if (part === undefined) return "there is no such part";
  return {
    ...score,
    id: createId("score"),
    meta: { ...score.meta, title: titleFor(score.meta.title, part.name) },
    layout: { ...score.layout, multiRests: true },
    parts: [part],
    groups: [],
  };
}
